import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import { Heading, isPlusPlan, cityToggleConfig } from '@spotahome/ui-library';

import {
  isAllBillsIncluded,
  isSomeBillsIncluded
} from '@spotahome/ui-library/src/utils/listing';
import { HomeCheckerShape } from '@spotahome/ui-library/src/tenant/shapes/HomeChecker';

import instantBookingShape from '@spotahome/marketplace-common/src/shapes/instantBooking';

import ShareModalWrapper from '@spotahome/marketplace-common/src/components/ShareModalWrapper';
import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';

import './PropertyTitle.scss';

import ListingLabels from '../ListingLabels';

import ListingDoubts from '../ListingDoubts';

import ListingDoubtsLandlord from '../ListingDoubtsLandlord/ListingDoubtsLandlord';

import PropertyDescriptionItem from './PropertyDescriptionItem';

const spainAndItalyCities = [
  'madrid',
  'barcelona',
  'valencia',
  'bilbao',
  'granada',
  'seville',
  'cordoba',
  'alicante',
  'salamanca',
  'murcia',
  'zaragoza',
  'malaga',
  'cadiz',
  'oviedo',
  'santiago_de_compostela',
  'alcala_de_henares',
  'las_palmas_de_gran_canaria',
  'valladolid',
  'vigo',
  'cartagena',
  'santander',
  'san_sebastian',
  'a_coruna',
  'huelva',
  'jaen',
  'pamplona',
  'palma_de_mallorca',
  'almeria',
  'rome',
  'milan',
  'florence',
  'bologna',
  'turin',
  'perugia',
  'naples',
  'padua',
  'genoa'
];

const disallowLandlordIds = [
  '3cc8a990-ca2f-485d-a950-50532e7e6362',
  '7ccf013e-6c63-4b4a-92cc-cac1e83ca107',
  'b40431ca-b977-4eae-a5ad-7b4a982e29ab',
  'da7cee22-9479-405c-8745-4eaa9c7cf5e5',
  '6b6b6cd6-2285-4f9b-9084-ec217f67fd32'
];

const PropertyTitle = ({
  title,
  type,
  details,
  cityId,
  isBestChoice,
  plan,
  bills = {},
  isChecked,
  instantBooking = {},
  allowsPermanentContract = false,
  homechecker = null,
  verificationDate = null,
  landlordName,
  landlordType,
  landlordId,
  listingId,
  listingAddress,
  listingUrl,
  countryCode,
  statistics = {}
}) => {
  const pricingContext = usePricingContext();

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const getPropertyType = () =>
    type === 'room_shared_apt' ? 'flatmates' : 'bedrooms';

  const contactLandlordExperimentSpainAndItaly = useSoyuzExperiment(
    'ListingPage_ContactLandlord_1'
  );

  const contactLandlordExperimentOtherCountries = useSoyuzExperiment(
    'ListingPage_ContactLandlord_2'
  );

  const showContactFormForItalyAndSpain =
    spainAndItalyCities.includes(cityId) &&
    !disallowLandlordIds.includes(landlordId);

  const showContactFormForOtherCountries =
    !spainAndItalyCities.includes(cityId) &&
    !disallowLandlordIds.includes(landlordId);

  const renderContactFormForExperiment = (shouldShowContactForm, experiment) =>
    shouldShowContactForm &&
    (experiment.get('behaviour') === 'variant' ? (
      <ListingDoubtsLandlord
        cityId={cityId}
        listingId={listingId}
        listingAddress={listingAddress}
        listingUrl={listingUrl}
        landlordId={landlordId}
        landlordName={landlordName}
        countryCode={countryCode}
      />
    ) : (
      <ListingDoubts
        cityId={cityId}
        listingId={listingId}
        countryCode={countryCode}
      />
    ));

  return (
    <div className="property-title property-title--compact">
      <Heading
        level={1}
        theme="dark"
        size="large"
        align="left"
        className="property-title__heading"
        text={title}
        inline
        type="none"
      />

      <div className="property-title__details">
        <span>{trans(`listing.type.${type}`)}</span>
        <PropertyDescriptionItem
          value={details.nBedrooms}
          text={trans(`listing.property-title-section.${getPropertyType()}`, {
            n: details.nBedrooms
          })}
        />
        <PropertyDescriptionItem
          value={details.nBathrooms}
          text={trans('listing.property-title-section.bathrooms', {
            n: details.nBathrooms
          })}
        />
        {details.m2 > 0 && (
          <PropertyDescriptionItem value={details.m2} text="m2" />
        )}
      </div>

      <div className="property-title__statistics">
        {statistics.visited > 0 && (
          <PropertyDescriptionItem
            value={statistics.visited}
            iconName="eye-filled"
            text={trans('listing.statistics.views')}
          />
        )}
        {statistics.favorite > 0 && (
          <PropertyDescriptionItem
            value={statistics.favorite}
            iconName="fav_filled"
            text={trans('listing.statistics.favorites')}
          />
        )}
        {statistics.interested > 0 && (
          <PropertyDescriptionItem
            value={statistics.interested}
            iconName="person-filled"
            text={trans('listing.statistics.interests')}
          />
        )}
        {statistics.shared > 0 && (
          <PropertyDescriptionItem
            value={statistics.shared}
            iconName="shared"
            text={trans('listing.statistics.shares')}
          />
        )}
      </div>

      <ListingLabels
        isChecked={isChecked}
        hasNoDeposit={pricingContext?.hasNoDeposit}
        instantBooking={instantBooking}
        allBillsIncluded={isAllBillsIncluded(bills)}
        someBillsIncluded={isSomeBillsIncluded(bills)}
        isBestChoice={cityToggleConfig(cityId).isCoreCity && isBestChoice}
        isPlusPlan={isPlusPlan(plan)}
        allowsPermanentContract={allowsPermanentContract}
        homechecker={homechecker}
        verificationDate={verificationDate}
        landlordType={landlordType}
        landlordId={landlordId}
      />
      <div ref={ref}>
        {renderContactFormForExperiment(
          showContactFormForItalyAndSpain && inView,
          contactLandlordExperimentSpainAndItaly
        )}
        {renderContactFormForExperiment(
          showContactFormForOtherCountries && inView,
          contactLandlordExperimentOtherCountries
        )}
      </div>
    </div>
  );
};

PropertyTitle.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  details: PropTypes.shape({
    nBedrooms: PropTypes.number,
    nBathrooms: PropTypes.number,
    m2: PropTypes.number
  }).isRequired,
  cityId: PropTypes.string.isRequired,
  isBestChoice: PropTypes.bool.isRequired,
  plan: PropTypes.string.isRequired,
  bills: PropTypes.shape({
    [PropTypes.number]: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }),
  isChecked: PropTypes.bool.isRequired,
  instantBooking: PropTypes.shape(instantBookingShape),
  allowsPermanentContract: PropTypes.bool,
  homechecker: PropTypes.shape(HomeCheckerShape),
  verificationDate: PropTypes.string,
  landlordType: PropTypes.oneOf(['professional', 'private']).isRequired,
  landlordId: PropTypes.string.isRequired,
  listingId: PropTypes.string.isRequired,
  listingAddress: PropTypes.string.isRequired,
  listingUrl: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  statistics: PropTypes.shape({
    visited: PropTypes.number,
    shared: PropTypes.number,
    favorite: PropTypes.number,
    interested: PropTypes.number
  })
};

export default PropertyTitle;
